import { useCrmData } from '../../../contexts/CrmProviders';
import { notify } from '../../../utils/toast';
import { Lead, Contact } from '../../../types';
import { ChatConversation } from './useChat';
import { useChatFavorites } from './useChatFavorites';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useContactPanelDealLeads } from './useContactPanelDealLeads';
import { useContactPanelDealStages } from './useContactPanelDealStages';
import { useCreateContactPanelDeal } from './useCreateContactPanelDeal';

export const useContactPanelDeals = (
    conversation: ChatConversation | undefined, 
    conversationTags: string[] = [], 
    conversationResponsible: string | null = null,
    currentContact: Contact | null = null
) => {
    const {
        leads,
        contacts,
        funnelStages,
        funnels,
        selectedFunnelId,
        updateLeadStage,
        saveLead,
        refreshContacts,
        createContact,
        setSelectedFunnelId,
    } = useCrmData();
    const { checkAndAutoUnfavorite } = useChatFavorites();
    const { currentOrganization } = useOrganization();
    const organizationId = currentOrganization?.id;
    const { currentLeads, refreshPanelLeads } = useContactPanelDealLeads({
        conversation,
        organizationId,
        currentContact,
        leads
    });
    const availableStages = useContactPanelDealStages({
        funnelStages,
        selectedFunnelId,
        funnels,
        organizationId
    });
    const { isCreatingDeal, handleCreateDeal } = useCreateContactPanelDeal({
        conversation,
        currentContact,
        contacts,
        organizationId,
        selectedFunnelId,
        funnels,
        conversationTags,
        conversationResponsible,
        createContact,
        refreshContacts,
        refreshPanelLeads,
        saveLead,
        setSelectedFunnelId
    });

    const handleStageChange = (leadId: string, newStageId: string) => {
        updateLeadStage(leadId, newStageId);

        window.dispatchEvent(new CustomEvent('chat:stage_changed', {
            detail: { leadId, newStageId }
        }));

        setTimeout(() => {
            refreshPanelLeads();
        }, 350);
    };

    const handleLeadUpdate = async (updatedLead: Lead) => {
        const previousLead = leads.find(l => l.id === updatedLead.id);
        const agentChanged = previousLead && previousLead.agent !== updatedLead.agent;

        if (agentChanged) {
            notify.info('🔄 Transferindo conversa...');
        }

        const saved = await saveLead(updatedLead);
        if (!saved) {
            return;
        }

        window.dispatchEvent(new CustomEvent('chat:stage_changed', {
            detail: { leadId: updatedLead.id },
        }));

        if (agentChanged && conversation?.id) {
            await checkAndAutoUnfavorite(conversation.id);
        }

        await refreshPanelLeads();
    };

    return {
        currentLeads,
        funnelStages: availableStages,
        isCreatingDeal,
        handleCreateDeal,
        handleStageChange,
        handleLeadUpdate
    };
};
